/** TOPIK 급수. 1~2 초급 / 3~4 중급 / 5~6 고급 */
export type TopikLevel = 1 | 2 | 3 | 4 | 5 | 6

export type Genre = 'kpop' | 'drama' | 'variety' | 'news'

/** 단어 뜻풀이 언어. 영어가 기본이자 폴백이다 (스토리보드 UI 3-1 "다국어 번역"). */
export type MeaningLang = 'en' | 'ja' | 'zh' | 'vi'

/** 고른 언어의 뜻풀이. 없으면 영어로 돌아간다 — 빈 칸을 보여주지 않는다. */
export function meaningFor(
  w: { meaningEn: string; meanings?: Partial<Record<MeaningLang, string>> },
  lang: MeaningLang | null,
): string {
  if (!lang || lang === 'en') return w.meaningEn
  return w.meanings?.[lang] ?? w.meaningEn
}

export const MEANING_LANGS: Array<{ value: MeaningLang; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'ja', label: '日本語' },
  { value: 'zh', label: '中文' },
  { value: 'vi', label: 'Tiếng Việt' },
]

export type VideoStatus = 'draft' | 'reviewed' | 'published'

/** 시험용 격식체 대비를 위한 화계 구분 */
export type Register = 'formal' | 'casual' | 'slang'

export interface Video {
  id: string
  /**
   * YouTube video id. 저작권 정책상 영상은 자체 호스팅하지 않고 IFrame 임베드만 한다.
   * null 이면 데모용 시뮬레이션 클럭으로 재생된다 (라이선스 확인 전 콘텐츠).
   */
  youtubeId: string | null
  title: string
  titleEn: string | null
  genre: Genre
  topikLevel: TopikLevel
  durationSec: number
  hasManualCc: boolean
  status: VideoStatus
  /** 홈 카드 메타 — "드라마 · 공식 클립 · 신규 단어 12" */
  newWordCount: number
  /**
   * 자막에 태깅된 단어 중 이 영상 급수 이하 어휘의 비율(%).
   * 태깅 전 영상은 null 이고, 이때는 뱃지를 띄우지 않는다 (스토리보드 UI 2).
   */
  vocabCoverage: number | null
  /**
   * 주소에 쓰는 읽을 수 있는 식별자. null 이면 화면이 id 로 폴백한다 —
   * UUID 를 그대로 노출하지 않으면서 기존 링크도 깨지지 않게 한다.
   */
  slug: string | null
}

export interface Word {
  id: number
  /** 사전 표제어(원형) */
  lemma: string
  /** 기본 뜻풀이이자, 고른 언어에 번역이 없을 때의 폴백 */
  meaningEn: string
  /** 언어코드 → 뜻풀이. 영어는 meaningEn 에 있으므로 여기 넣지 않는다. 없으면 영어로 폴백. */
  meanings?: Partial<Record<MeaningLang, string>>
  topikLevel: TopikLevel
  register: Register | null
  /**
   * 표준 발음 표기. 한국어는 대부분 표기대로 읽으므로, 표기와 발음이 다를 때만 채운다
   * (스토리보드 UI 3-1 — 원형·발음·뜻을 최상단에).
   */
  pronunciation: string | null
  /** 시험용 격식체 변환형 */
  examForm: string | null
  /** TOPIK TIP — 반말·유행어 ↔ 시험용 격식체 비교 */
  tip: string | null
  /**
   * TOPIK 시험용 예문. 자막(=실생활)과 짝을 이뤄 화계 차이를 문장 단위로 보여준다.
   * 검수를 통과한 것만 내려온다 (RLS 가 reviewed 로 거른다).
   */
  example?: { sentence: string; translationEn: string } | null
}

export interface SubtitleLine {
  id: number
  videoId: string
  idx: number
  startSec: number
  endSec: number
  text: string
  /** 영어 번역이자, 고른 언어에 번역이 없을 때의 폴백 */
  textEn: string | null
  /** 언어코드 → 번역. 영어는 textEn 에 있으므로 여기 넣지 않는다. */
  translations?: Partial<Record<MeaningLang, string>>
}

/**
 * 자막 번역 표시 방식.
 *   lang    (기본) 고른 언어 한 줄
 *   lang_en 고른 언어 + 영어
 *   off     한국어만 — 실력이 올라가면 번역을 가리고 싶어진다
 *
 * 영상은 3~5초마다 줄이 통째로 바뀐다. 기본을 한 줄로 둔 이유는 PM 코멘트의
 * "시선 분산 최소화" 때문이고, 더 보고 싶은 사람에게만 두 줄을 연다.
 */
export type SubtitleMode = 'lang' | 'lang_en' | 'off'

export const SUBTITLE_MODES: Array<{ value: SubtitleMode; label: string }> = [
  { value: 'lang', label: '모국어만' },
  { value: 'lang_en', label: '모국어 + 영어' },
  { value: 'off', label: '끄기' },
]

/** 자막 아래에 그릴 번역 줄. 0~2줄이 나온다. */
export function subtitleTranslations(
  line: { textEn: string | null; translations?: Partial<Record<MeaningLang, string>> },
  lang: MeaningLang | null,
  mode: SubtitleMode,
): string[] {
  if (mode === 'off') return []
  const primary =
    !lang || lang === 'en' ? line.textEn : (line.translations?.[lang] ?? line.textEn)
  if (!primary) return []
  if (mode === 'lang_en' && line.textEn && line.textEn !== primary) return [primary, line.textEn]
  return [primary]
}

/** 자막 토큰 ↔ 사전 매핑. 토큰은 자막 원문에 등장한 활용형. */
export interface LineWord {
  lineId: number
  token: string
  wordId: number
}

/** 영상 진입 시 1회 fetch 해 클라이언트에서 캐시하는 묶음 */
export interface VideoScript {
  video: Video
  lines: SubtitleLine[]
  words: Record<number, Word>
  /** lineId → (token → wordId) */
  tokens: Record<number, Record<string, number>>
}

export interface SavedWord {
  id: string
  wordId: number
  lemma: string
  meaningEn: string
  topikLevel: TopikLevel
  meanings?: Partial<Record<MeaningLang, string>>
  /** 저장 당시 자막 원문 */
  sentence: string
  videoId: string
  /**
   * 저장 시점 영상의 YouTube id. 단어장에서 화면 전환 없이 그 자리에서
   * 클립을 다시 듣기 위해 함께 들고 온다 (스토리보드 UI 4 · 인라인 재생).
   * null 이면 시뮬레이션 클럭으로 구간만 표시한다.
   */
  youtubeId: string | null
  lineId: number
  /** 클립 시작점 = 자막 줄의 시작 */
  startSec: number
  /**
   * 자막 줄의 끝. 단어장에서 문장을 **끝까지** 들려주기 위해 함께 들고 온다 —
   * 3초로 자르면 말이 중간에서 끊겨 억양을 확인할 수 없다.
   * 옛 저장분이나 매핑이 없는 줄은 null 이고, 이때만 3초로 폴백한다.
   */
  endSec: number | null
  /**
   * 이 단어의 TOPIK 시험용 예문. 자막(=회화)과 짝으로 보여준다.
   * 검수를 통과한 것만 내려온다 (RLS).
   */
  example?: { sentence: string; translationEn: string } | null
  /** 암기 완료 — 목록 하단으로 밀린다 (스토리보드 UI 4) */
  mastered: boolean
  createdAt: string
}

/** 학습 탭의 "이어서 학습하기" 카드 */
export interface ContinueWatching {
  video: Video
  positionSec: number
  /** 0~100. 영상 길이를 모르면(=0) 0 으로 둔다 */
  percent: number
}

export interface QuizQuestion {
  id: number
  tag: string
  source: string
  prompt: string
  promptEn: string
  options: Array<{ ko: string; en: string }>
  answerIdx: number
  /** 이 문항을 맞혔을 때 가중되는 급수 */
  weightLevel: TopikLevel
  /**
   * 듣기 문항. 지문을 **글로 보여주지 않는다** — 보이면 읽기 문항이 되고,
   * 결과 리포트의 '듣기 취약' 진단도 근거를 잃는다.
   */
  isListening?: boolean
  /** 미리 만들어 둔 음성 파일. 없으면 지문을 글로 보여준다 (폴백) */
  audioUrl?: string | null
}

/**
 * 레벨 테스트 결과 리포트 한 구간 (level_report_cases).
 *
 * 맞힌 개수로 고른다 — 가중치 계산이 아니라 구간표다. 문장은 전부 사람이 써서 DB 에
 * 넣어 둔 것이고, 화면은 골라 읽기만 한다 (제약 2 · 런타임 LLM 없음).
 */
export interface LevelReport {
  code: string
  topikLevel: TopikLevel
  correctCount: number
  total: number
  /** 고른 언어의 문구. 없으면 영어, 그것도 없으면 한국어로 폴백한다 */
  copy: LevelReportCopy
  /** 병기용 — copy 가 한국어면 여기에 영어가 온다 (같으면 null) */
  copyAlt: LevelReportCopy | null
}

export interface LevelReportCopy {
  predicted: string
  feel: string
  summary: string
  strengths: string
  basis: string
  weak1: string
  weak1_desc: string
  weak2: string
  weak2_desc: string
  tip: string
  goal: string
  step1: string
  step2: string
  cta1: string
  cta2: string
}

export interface DiagnosisResult {
  level: TopikLevel
  correctCount: number
  total: number
  /** 결과 리포트의 성취 카드 */
  achievements: Array<{
    title: string
    titleEn: string
    percent: number
    muted?: boolean
  }>
  summaryKo: string
  summaryEn: string
}

export interface Profile {
  id: string
  displayName: string
  topikLevel: TopikLevel
  preferredGenres: Genre[]
  /**
   * 어드민 여부. 화면에서 어드민 진입점을 감추는 용도이고, 실제 차단은 RLS 가 한다.
   * 사용자가 스스로 켤 수 없다 (컬럼 UPDATE 권한 회수).
   */
  isAdmin: boolean
  /** 단어 뜻풀이를 보여줄 언어. null 이면 영어 */
  meaningLang: MeaningLang | null
  /** 자막 번역 표시 방식. null 이면 'lang' */
  subtitleMode: SubtitleMode | null
}

/**
 * 학습 탭의 "오늘의 과제" (핵심 원칙 Micro-Bite — 5분 이내 단위로 쪼갠다).
 * 별도 집계 테이블 없이 오늘 남은 기록에서 센다.
 */
export interface StudyToday {
  /** 연속 학습 일수 */
  streak: number
  /** 오늘 재생 위치를 남긴 영상 수 */
  watched: number
  /** 오늘 저장한 단어 수 */
  saved: number
  /** 오늘 암기 체크한 단어 수 */
  mastered: number
  /** 오늘 복습한 단어 수 */
  reviewed: number
  /** 지금 복습할 수 있는 단어 수 */
  due: number
}

export const DAILY_GOAL = { watched: 1, saved: 3, reviewed: 5 } as const

/**
 * 복습 문제 한 장. 저장한 단어가 **실제로 나왔던 문장**에서 그 활용형만 비운다.
 * 문제를 따로 만들지 않으므로 콘텐츠 비용이 들지 않는다.
 */
export interface ReviewCard {
  savedWordId: string
  wordId: number
  lemma: string
  meaningEn: string
  meanings?: Partial<Record<MeaningLang, string>>
  topikLevel: TopikLevel
  /** 자막 원문 */
  sentence: string
  /** 그 문장에 등장한 활용형 — 이 자리를 빈칸으로 만든다 */
  token: string
  startSec: number
  /** 자막 줄의 끝 — 정답 확인 때 문장을 끝까지 들려준다 (단어장과 같은 규칙) */
  endSec: number | null
  videoId: string
  youtubeId: string | null
  /** 보기(정답 포함) */
  options: string[]
  answerIdx: number
}

/* ---------------------------------------------------------------- 실력 점검 --- */

/**
 * 문항 종류.
 *   meaning   어휘 뜻 고르기        — TOPIK 급수별 어휘에서
 *   cloze     예문 빈칸 채우기      — 시험용 예문에서
 *   register  화계 변환             — 자막(반말) → 시험 답안 형태. 우리만 낼 수 있는 문항이다
 *   listening 듣고 고르기           — 자막 구간 3초를 듣고 고른다
 */
export type ExamKind = 'meaning' | 'cloze' | 'register' | 'listening'

export const EXAM_KIND_LABEL: Record<ExamKind, string> = {
  meaning: '어휘',
  cloze: '문장',
  register: '화계',
  listening: '듣기',
}

export interface ExamQuestion {
  id: string
  kind: ExamKind
  /** 지시문 */
  prompt: string
  /** 지문 (문장·단어). 듣기 문항에는 없다 */
  passage?: string
  youtubeId?: string | null
  startSec?: number
  /** 듣기 문항 — 자막 줄의 끝. 3초로 자르면 문장이 안 끝나 고를 수가 없다 */
  endSec?: number | null
  options: string[]
  answerIdx: number
  /** 채점 후 해설 */
  explain?: string
  level: number
}

/** 실력 점검 결과 — 급수를 말없이 덮어쓰지 않고 제안만 한다 */
export interface ExamResult {
  correct: number
  total: number
  /** 문항 급수 가중으로 계산한 예상 급수 */
  suggestedLevel: TopikLevel
  byKind: Array<{ kind: ExamKind; correct: number; total: number }>
}

/**
 * 실력 점검을 여러 번 본 결과를 종류별로 합친 것 — '약점 영역'.
 *
 * 지금까지 점검 결과는 그 화면에서 한 번 보여주고 버려졌다(기록은 남기지만 아무도
 * 다시 읽지 않았다). 한 회차 10문항으로는 "어휘가 약하다"를 말할 수 없지만,
 * 여러 회차를 합치면 말할 수 있다. 새 데이터를 만들지 않고 있던 기록만 읽는다.
 */
export interface ExamHistory {
  /** 합산에 쓴 회차 수 */
  attempts: number
  /** 가장 최근 점검 시각 (ISO). 없으면 null */
  lastAt: string | null
  /** 종류별 누적. 한 번도 안 나온 종류는 들어 있지 않다 */
  areas: Array<{ kind: ExamKind; correct: number; total: number }>
}

/** 약점 판정 기준 — 이 아래면 '약함', 위쪽 기준을 넘으면 '괜찮음' */
export const WEAK_PCT = 50
export const STRONG_PCT = 75

/** 최근 몇 회차까지 합쳐 볼 것인가. 오래된 회차가 지금 실력을 가리지 않게 한다. */
export const EXAM_HISTORY_ATTEMPTS = 10

/** 한 회차 문항 수. 실제 TOPIK 은 두 시간이지만 그걸 흉내 내면 아무도 하지 않는다. */
export const EXAM_SIZE = 10

/** 간격 반복. 맞히면 다음 간격으로, 틀리면 처음으로 돌아간다. */
export const REVIEW_INTERVALS = [1, 3, 7, 16, 35] as const
/** 이만큼 연속으로 맞히면 '암기 완료'로 넘긴다 — 체크박스는 선언, 이건 확인이다. */
export const MASTER_STREAK = 4

/** 플레이어 재생 상태 — YouTube IFrame Player 와 시뮬레이션 클럭의 공통 인터페이스 */
export interface MediaClock {
  time: number
  duration: number
  playing: boolean
  ready: boolean
  error: boolean
  play(): void
  pause(): void
  toggle(): void
  seekTo(sec: number, autoplay?: boolean): void
  /**
   * 한 구간만 재생한다 (단어장·복습·단어 시트가 모두 이걸 쓴다).
   *
   * 멈추는 시점은 **재생 위치**로 판단한다. setTimeout 으로 재면 탐색·버퍼링에 쓴
   * 시간까지 세어 문장이 중간에서 끊긴다 — 실제로 그렇게 새고 있었다.
   */
  playClip(startSec: number, endSec: number | null): void
  /** 구간 재생이 진행 중인지. 끝나면 스스로 false 가 된다. */
  clipActive: boolean
}
